import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Prompt Queue System
 * 
 * Ensures prompts appear one at a time in a logical order:
 * 1. AI Consent (most important - required for core functionality)
 * 2. Onboarding Tour (orientation)
 * 3. HealthKit prompt (feature-specific)
 * 4. Notifications (lowest priority)
 * 
 * Each prompt must be dismissed before the next one appears.
 * 
 * Key fix: Tracks dismissed prompts within the session to prevent
 * re-showing a prompt that was just dismissed (even if the condition
 * is still true because async state hasn't updated yet).
 */

export type PromptType = 'ai_consent' | 'tour' | 'health_onboarding' | 'health_first_workout' | 'notifications';

interface PromptQueueState {
  activePrompt: PromptType | null;
  dismissedThisSession: Set<PromptType>;
}

interface UsePromptQueueOptions {
  // Conditions for each prompt type
  shouldShowAIConsent: boolean;
  shouldShowTour: boolean;
  shouldShowHealthOnboarding: boolean;
  shouldShowHealthFirstWorkout: boolean;
  shouldShowNotifications: boolean;
  
  // Loading states - don't process queue until ready
  isLoading: boolean;
}

export function usePromptQueue({
  shouldShowAIConsent,
  shouldShowTour,
  shouldShowHealthOnboarding,
  shouldShowHealthFirstWorkout,
  shouldShowNotifications,
  isLoading,
}: UsePromptQueueOptions) {
  const [state, setState] = useState<PromptQueueState>({
    activePrompt: null,
    dismissedThisSession: new Set(),
  });
  
  // Track if we've processed the initial queue
  const hasProcessedInitialQueue = useRef(false);

  // Build the queue based on current conditions (priority order)
  // Excludes prompts that were already dismissed this session
  const buildQueue = useCallback((): PromptType[] => {
    const queue: PromptType[] = [];
    const dismissed = state.dismissedThisSession;
    
    // Priority 1: AI Consent (required for app functionality)
    if (shouldShowAIConsent && !dismissed.has('ai_consent')) {
      queue.push('ai_consent');
    }
    
    // Priority 2: Onboarding Tour
    if (shouldShowTour && !dismissed.has('tour')) {
      queue.push('tour');
    }
    
    // Priority 3: HealthKit prompts (only one at a time)
    if (shouldShowHealthOnboarding && !dismissed.has('health_onboarding')) {
      queue.push('health_onboarding');
    } else if (shouldShowHealthFirstWorkout && !dismissed.has('health_first_workout')) {
      queue.push('health_first_workout');
    }
    
    // Priority 4: Notifications (lowest priority)
    if (shouldShowNotifications && !dismissed.has('notifications')) {
      queue.push('notifications');
    }
    
    return queue;
  }, [shouldShowAIConsent, shouldShowTour, shouldShowHealthOnboarding, shouldShowHealthFirstWorkout, shouldShowNotifications, state.dismissedThisSession]);

  // Process the queue - show the first pending prompt
  useEffect(() => {
    if (isLoading) return;
    
    // Only process if there's no active prompt
    if (state.activePrompt !== null) return;
    
    const queue = buildQueue();
    
    // Show the first available prompt
    if (queue.length > 0) {
      // Add small delay on initial load for smoother UX
      const delay = hasProcessedInitialQueue.current ? 300 : 500;
      
      const timer = setTimeout(() => {
        setState(prev => ({
          ...prev,
          activePrompt: queue[0],
        }));
      }, delay);
      
      hasProcessedInitialQueue.current = true;
      return () => clearTimeout(timer);
    }
  }, [isLoading, buildQueue, state.activePrompt]);

  // Dismiss the current prompt and mark as dismissed for this session
  const dismissPrompt = useCallback((promptType: PromptType) => {
    setState(prev => {
      // Only process if this is the active prompt
      if (prev.activePrompt !== promptType) {
        return prev;
      }
      
      // Add to dismissed set to prevent re-showing
      const newDismissed = new Set(prev.dismissedThisSession);
      newDismissed.add(promptType);
      
      return {
        activePrompt: null,
        dismissedThisSession: newDismissed,
      };
    });
  }, []);

  // Check if a specific prompt should be shown
  const isPromptActive = useCallback((promptType: PromptType): boolean => {
    return state.activePrompt === promptType;
  }, [state.activePrompt]);

  // Force show a specific prompt (for manual triggers like settings)
  // This removes the prompt from dismissed set first
  const forceShowPrompt = useCallback((promptType: PromptType) => {
    setState(prev => {
      const newDismissed = new Set(prev.dismissedThisSession);
      newDismissed.delete(promptType); // Allow re-showing
      
      return {
        activePrompt: promptType,
        dismissedThisSession: newDismissed,
      };
    });
  }, []);

  return {
    activePrompt: state.activePrompt,
    isPromptActive,
    dismissPrompt,
    forceShowPrompt,
    hasActivePrompt: state.activePrompt !== null,
  };
}
